import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { toast } from 'sonner'
import { personalKeycardAPI } from '#/api/personalKeycard'


interface FormInputs {
  id: string
}

interface DeletePersonalKeycardModalProps {
  id: string
  onClose: () => void
}

export function DeletePersonalKeycardModal({ id, onClose }: DeletePersonalKeycardModalProps) {
  const { t } = useTranslation([ 'personal_keycards' ])
  const { organization } = useParams()
  const queryClient = useQueryClient()

  const form = useForm<FormInputs>({
    defaultValues: {
      id: ''
    }
  })

  const removePersonalKeycardMutation = useMutation({
    mutationFn: () => personalKeycardAPI.delete(id),
    onSuccess: () => {
      // Invalidate the specific personal keycard and the personal keycard list
      queryClient.invalidateQueries({ queryKey: [ 'organizations', organization, 'personalKeycards' ] })
      queryClient.removeQueries({
        queryKey: [ 'organizations', organization, 'personalKeycards', id ]
      })
      toast.success(t('personal_keycard_list.menu.toasts.remove_success'))
      onClose()
    },
    onError: () => {
      // handled via AlertBox
    }
  })

  const handleSubmit = form.handleSubmit(values => {
    if ((values.id ?? '').trim() !== id.trim()) {
      form.setError('id', {
        type: 'manual',
        message: t('personal_keycard_list.menu.toasts.remove_mismatch')
      })

      return
    }

    removePersonalKeycardMutation.mutate()
  })

  return (
    <Dialog open modal onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('personal_keycard_list.menu.remove_title', { id })}</DialogTitle>
          <DialogDescription>{t('personal_keycard_list.menu.remove_description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {removePersonalKeycardMutation.error && (
              <AlertBox
                error
                title={mutationError(removePersonalKeycardMutation.error, {
                  default: t('personal_keycard_list.menu.toasts.remove_error')
                })}
              />
            )}

            <FormField
              name="id"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('personal_keycard_list.menu.remove_field')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={128}
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('personal_keycard_list.menu.remove_cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={removePersonalKeycardMutation.isPending}
              >
                {t('personal_keycard_list.menu.remove_submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
